<script setup lang="ts">
    import { computed, onMounted, reactive, ref } from 'vue';
    import { 
        type AiRequest,
        getTones,
        getHeadingTypes,
        AiDropdownOption,
        getOrderedLanguageOptions,
        LanguageOptionParameters,
    } from '@cwd/AiTools/AiEditor.access';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import Modal from '@shared/components/Modal.vue';
    import { setupShared } from '@cwd/AiTools/Shared';
    import Terms from '@cwd/AiTools/Terms.vue';
    import Intro from '@cwd/AiTools/Intro.vue';
    import LanguageOptionDropdownWithFilter from '@shared/components/LanguageOptionDropdownWithFilter.vue';

    const {
        shared,
        disabled,
        firstLetterToUpperCase,
        acceptTerms,
        resetOptions,
        showConsumption,
        getFormattedConsumption,
        updateConsumption,
        makeRequest,
        makeOptionsRequest,
    } = setupShared();

    const props = defineProps<{
        editor: {
            contentEditable: any;
            sync: () => void;
            show: any;
        };
    }>();

    const languageOptions = ref<AiDropdownOption[]>([]);
    const languageSearchTerm = ref('');
    const defaultLanguage: LanguageOptionParameters = {
        language: 'Swedish',
        culture: 'sv-SE',
        sourceLanguage: '',
    };
    const selectedLanguage = ref<LanguageOptionParameters>(defaultLanguage);
    const filteredLanguageOptions = computed(() => {
        if (languageSearchTerm.value) {
            const searchTerm = languageSearchTerm.value.toLowerCase();
            return languageOptions.value.filter((option: AiDropdownOption) => 
                option.Parameters.toString().toLowerCase().includes(searchTerm)
            );
        }
        return languageOptions.value;
    });

    onMounted(async () => {
        console.log('getLanguageOptions() AiEditor.vue onMounted');
        languageOptions.value = await getOrderedLanguageOptions();

        props.editor.show &&
            props.editor.show.subscribe((newValue: boolean) => {
                shared.show = newValue;
                if (newValue === true) init();
            });
        }
    );
    
    const state = reactive<{
        introShown: boolean;
    }>({
        introShown: true,
    });

    const commandText = (): string => {
        return shared.current === null ? shared.source : shared.results[shared.current].resultText;
    };

    const init = async () => {
        setTimeout(() => (state.introShown = true), 4000);

        shared.results = [];
        shared.resultOptions = [];
        shared.current = null;
        shared.source = props.editor.contentEditable[0].innerHTML;

        await updateConsumption();
    };

    const useOption = (option: string) => {
        switch (shared.command) {
            case 'heading':
                updateResult('<h2>' + option + '</h2>' + commandText());
                break;
        }
    };

    const updateResults = (results: string[]) => {
        updateResult(results[0]);
    };

    const updateResult = (result: string) => {
        shared.current !== null && (shared.results.length = shared.current + 1);
        shared.results.push({
            resultText: result,
            request: shared.request as AiRequest,
        });
        shared.current = shared.results.length - 1;
        shared.resultOptions = [];
    };

    const insert = () => {
        if (shared.current == null) return;

        props.editor.contentEditable[0].innerHTML = shared.results[shared.current as number].resultText;
        props.editor.sync();
        props.editor.show(false);
    };

    const getRequestInfo = (request: AiRequest) => {
        return $Resources['AIAssistant' + firstLetterToUpperCase(request.Command)];
    };
</script>

<script lang="ts"></script>

<template>
    <Modal :title="$Resources.AiAssistantCreateSurveyTitle"
           :beta="false"
           icon="ai"
           :show="shared.show"
           size="lg"
           @close="props.editor.show(false)"
           :scrollBody="false">
        <template #content>
            <Intro v-show="!state.introShown"></Intro>

            <div class="ai-fade-in" v-show="state.introShown">
                <Terms :termsAccepted="shared.termsAccepted" @accept="acceptTerms()"></Terms>

                <div class="m-lg-b">
                    <div class="ai-initial" :class="{ 'ai-loading': shared.loading && shared.current === null }">
                        <div v-html="shared.source"></div>
                    </div>
                </div>

                <!-- AI Options -->
                <div v-show="shared.resultOptions.length === 0">
                    <h3 class="h5">{{ $Resources.AIAssistantUseAiTo }}:</h3>

                    <div class="form-group d-flex flex-wrap gap-sm btn-group-rounded">
                        <!-- Shorten -->
                        <button type="button" class="btn btn-default"
                                @click="makeRequest('shorten', commandText(), updateResults)" :disabled="disabled()">
                            {{ $Resources.AIAssistantShorten }}
                        </button>

                        <!-- Lengthen -->
                        <button type="button" class="btn btn-default"
                                @click="makeRequest('lengthen', commandText(), updateResults)" :disabled="disabled()">
                            {{ $Resources.AIAssistantLengthen }}
                        </button>

                        <!-- Simplify -->
                        <button type="button" class="btn btn-default"
                                @click="makeRequest('simplify', commandText(), updateResults)" :disabled="disabled()">
                            {{ $Resources.AIAssistantSimplify }}
                        </button>
                        
                        <!-- Translation / Language Selection -->
                        <LanguageOptionDropdownWithFilter
                            :placeholderOption="$Resources.AIAssistantTranslate"
                            :languageOptions="languageOptions"
                            :visibleLanguageOptionCount="12"
                            :defaultLanguageOption="({ language: selectedLanguage.language, culture: selectedLanguage.culture } as LanguageOptionParameters)"
                            :modelValue="({ language: selectedLanguage.language, culture: selectedLanguage.culture } as LanguageOptionParameters)"
                            :sourceLanguage="selectedLanguage.language" 
                            @update:modelValue="(languageOption) => { 
                                selectedLanguage = languageOption; 
                                makeRequest('translate', commandText(), updateResults, { 
                                    language: languageOption.language,
                                    culture: languageOption.culture,
                                    sourceLanguage: languageOption.sourceLanguage || selectedLanguage.language
                                }); 
                            }" 
                            :disabled="disabled()"
                            rounded />
                            
                        <!-- Tone -->
                        <BSDropdownButton menuClass="dropdown-menu-right" :closeOnClickInside="true" :disabled="disabled()">
                            <template #toggle>
                                {{ $Resources.AIAssistantTone }}
                                <span class="caret m-xs-l"></span>
                            </template>
                            <template #content>
                                <li v-for="option in getTones()">
                                    <a href="javascript:void(0)"
                                       @click="makeRequest(option.Command, commandText(), updateResults, option.Parameters)">
                                        {{ option.Text }}
                                    </a>
                                </li>
                            </template>
                        </BSDropdownButton>

                        <!-- Headings -->
                        <BSDropdownButton menuClass="dropdown-menu-right" :closeOnClickInside="true" :disabled="disabled()">
                            <template #toggle>
                                {{ $Resources.AIAssistantHeading }}
                                <span class="caret m-xs-l"></span>
                            </template>
                            <template #content>
                                <li v-for="option in getHeadingTypes()">
                                    <a href="javascript:void(0)"
                                       @click="makeOptionsRequest(option.Command, commandText(), option.Parameters)">
                                        {{ option.Text }}
                                    </a>
                                </li>
                            </template>
                        </BSDropdownButton>

                        <!-- Spelling -->
                        <button type="button" class="btn btn-default"
                                @click="makeRequest('spelling', commandText(), updateResults)" :disabled="disabled()">
                            {{ $Resources.AIAssistantSpelling }}
                        </button>
                    </div>
                </div>

                <div v-show="shared.resultOptions.length > 0">
                    <h3 class="h5">{{ $Resources.AIAssistantSuggestions }}:</h3>

                    <ul class="list-unstyled m-lg-b">
                        <template v-for="(option, index) in shared.resultOptions">
                            <li>
                                <button type="button"
                                        class="btn btn-default btn-block m-xs-b text-left text-wrap"
                                        @click="useOption(option)"
                                        :disabled="disabled()">
                                    {{ index + 1 + '. ' + option }}
                                </button>
                            </li>
                        </template>
                        <li class="d-flex justify-content-between">
                            <button type="button" class="btn btn-subtle"
                                    @click="resetOptions">
                                {{ $Resources.CancelBtn }}
                            </button>
                        </li>
                    </ul>
                </div>

                <div v-if="shared.current !== null">
                    <h3 class="h5 d-flex">
                        <span class="m-auto-r">{{ $Resources.AIAssistantEnhancedText }}:</span>
                        {{ getRequestInfo(shared.results[shared.current].request) }}
                    </h3>

                    <div class="m-lg-b">
                        <div class="ai-result" :class="{ 'ai-loading': shared.loading }">
                            <div v-html="shared.results[shared.current].resultText"></div>
                        </div>

                        <div class="d-flex flex-wrap gap-xs m-sm-t">
                            <template v-for="(result, index) in shared.results">
                                <button v-show="shared.results.length > 1"
                                        type="button"
                                        class="btn btn-default btn-sm"
                                        :disabled="shared.loading || shared.resultOptions.length > 0"
                                        :class="{ 'btn-primary': index === shared.current }"
                                        @click="shared.current = index"
                                        :title="getRequestInfo(result.request)">
                                    {{ index + 1 }}
                                </button>
                            </template>

                            <button type="button"
                                    class="btn btn-default btn-sm m-auto-l"
                                    :disabled="shared.loading || shared.resultOptions.length > 0"
                                    @click="init">
                                {{ $Resources.AIAssistantRestartButton }}
                            </button>
                        </div>
                    </div>
                </div>

                <div>{{ $Resources.AIAssistantTipDoItManyTimes }}</div>
                <div v-if="showConsumption()">
                    <span>{{ getFormattedConsumption() }} % {{ $Resources.AIAssistantConsumptionPostText }}</span>
                </div>
            </div>
        </template>
        <template #button>
            <button
                type="button"
                class="btn btn-primary p-lg-r p-lg-l"
                :disabled="disabled() || shared.resultOptions.length > 0 || shared.current == null"
                @click="insert">
                {{ $Resources.AIAssistantInsertButton }}
            </button>
        </template>
    </Modal>
</template>

<style lang="less">
    .ai-fade-in {
        animation: fade-in 500ms linear 0ms 1 forwards;
        opacity: 0;
        z-index: 1;
        position: relative;

        @keyframes fade-in {
            0% {
                opacity: 0;
            }
            100% {
                opacity: 1;
            }
        }
    }
    
    /* Style language dropdown to match button size and appearance */
    .btn-group-rounded .form-control.custom-select-display {
        // height: 34px;
        padding: 6px 12px;
        color: #222234;
        border-color: #ccc;
        background-color: #fff;
        box-shadow: none;
        width: auto;
        min-width: 160px;
    }
    
    .btn-group-rounded .form-control.custom-select-display.rounded {
        border-radius: 999px;
    }
    
    .btn-group-rounded .form-control.custom-select-display:hover {
        background-color: #e6e6e6;
        border-color: #adadad;
    }

    .language-dropdown-menu {
        max-height: 300px;
        overflow-y: auto;
        padding: 0;
        margin: 0;

        li {
            padding: 0;
            margin: 0;
            
            a {
                display: block;
                padding: 6px 20px;
                clear: both;
                font-weight: normal;
                line-height: 1.42857143;
                white-space: nowrap;
            }
            
            a:hover {
                background-color: var(--brand-primary-500, #337ab7);
                color: #fff;
                text-decoration: none;
            }
        }
    }

    .position-relative {
        position: relative;
    }

    .clear-search-btn {
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        width: 2rem;
        border: none;
        background: transparent;
        font-size: 1.5rem;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        color: #6c757d;
    }
    
    .clear-search-btn:hover {
        color: #212529;
    }
</style>

<style scoped lang="less"></style>
